import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import {
  FormField,
  AmountPercentInput,
  NightsInput,
  PerMonthInput,
  YearsInput,
  Text,
  InfoButton,
  Alert,
  Button,
} from '../../../../../../components'
import { INPUT_NAMES, MODAL_FIELD_INFO } from '../../../../../../constants/display'
import DownPaymentHint from './DownPaymentHint'
import DownPaymentSubtext from './DownPaymentSubtext'

import { useListingDisplay } from '../../../../../../hooks/listing'

import { PMI_DROPPED_PERCENT_AS_DECIMAL } from '../../../../../../constants/buyerSettings'
import { ModalContext } from '../../../../../../contexts/ModalContext'
import { FormHeader } from '../FormHeader'

import styles from './HomeownerForm.module.scss'
import { FormModal } from '../FormModal'

const HomeownerForm = ({
  onSubmit,
  onChange,
  isSubmitting,
  values,
  errors,
  focusedField,
  ...restProps
}) => {
  const { showInfoModal, setShowInfoModal, setFieldInfo } =
    useContext(ModalContext)
  const { listingPrice, currentAddress } = useListingDisplay()

  const showDownPaymentSubtext =
    values &&
    values[INPUT_NAMES.DOWN_PAYMENT] <
      listingPrice * PMI_DROPPED_PERCENT_AS_DECIMAL

  const handleInfoClick = field => {
    setFieldInfo(field)
    setShowInfoModal(true)
  }

  return (
    <>
      <form className={styles.homeownerForm} onSubmit={onSubmit}>
        <FormHeader
          isLoading={isSubmitting}
          btnNode={
            <Button
              minimal
              type='submit'
              loading={isSubmitting}
              className={styles.semiBold}
            >
              Update
            </Button>
          }
        />
        {errors && errors.blank && (
          <div className={styles.alert}>
            <Alert type='danger'>
              <Alert.Message>{errors.blank}</Alert.Message>
            </Alert>
          </div>
        )}
        <div
          className={classNames(
            styles.formBody,
            errors && errors.blank && styles.formBody_errorSpacing
          )}
        >
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <Text element='span' weight='bold'>
                For{' '}
              </Text>
              <Text element='span' weight='bold'>
                {currentAddress || 'Your current address'}
              </Text>
            </div>
            <div className={styles.sectionBody}>
              <FormField
                value={values && values[INPUT_NAMES.CURRENT_MONTHLY_COST]}
                placeholder='$'
                name={INPUT_NAMES.CURRENT_MONTHLY_COST}
                label='Current monthly home costs'
                labelIconButton={
                  <InfoButton
                    className={styles.infoIcons}
                    onClick={() =>
                      handleInfoClick(
                        MODAL_FIELD_INFO.CURRENT_MONTHLY_HOME_COSTS
                      )
                    }
                  />
                }
                input={PerMonthInput}
                error={errors && errors[INPUT_NAMES.CURRENT_MONTHLY_COST]}
                onChange={onChange}
                autoFocus={focusedField === INPUT_NAMES.CURRENT_MONTHLY_COST}
                {...restProps}
              />
            </div>
          </section>
          <section className={styles.section}>
            {/* todo - ask ngoc why is this class used twice here?? */}
            <div className={styles.sectionHeader}>
              <Text
                element='span'
                weight='bold'
                className={styles.sectionHeader}
              >
                For this home
              </Text>
            </div>
            <div className={styles.sectionBody}>
              <FormField
                containerClassNames={styles.formField}
                value={values && values[INPUT_NAMES.MAX_BUDGET]}
                placeholder='$'
                name={INPUT_NAMES.MAX_BUDGET}
                label='Future monthly home costs'
                labelIconButton={
                  <InfoButton
                    className={styles.infoIcons}
                    onClick={() =>
                      handleInfoClick(
                        MODAL_FIELD_INFO.FUTURE_MONTHLY_HOME_COSTS
                      )
                    }
                  />
                }
                input={PerMonthInput}
                error={errors && errors[INPUT_NAMES.MAX_BUDGET]}
                autoFocus={focusedField === INPUT_NAMES.MAX_BUDGET}
                onChange={onChange}
                {...restProps}
              />

              <FormField
                containerClassNames={styles.formField}
                total={listingPrice}
                amount={values && parseFloat(values[INPUT_NAMES.DOWN_PAYMENT])}
                placeholder='$'
                name={INPUT_NAMES.DOWN_PAYMENT}
                label='Down payment'
                labelIconButton={
                  <InfoButton
                    className={styles.infoIcons}
                    onClick={() =>
                      handleInfoClick(MODAL_FIELD_INFO.DOWN_PAYMENT)
                    }
                  />
                }
                hint={<DownPaymentHint />}
                input={AmountPercentInput}
                subtext={showDownPaymentSubtext && <DownPaymentSubtext />}
                error={errors && errors[INPUT_NAMES.DOWN_PAYMENT]}
                onChange={onChange}
                autoFocus={focusedField === INPUT_NAMES.DOWN_PAYMENT}
                {...restProps}
              />

              <FormField
                containerClassNames={styles.formField}
                className={styles.yearsInHome}
                value={values && values[INPUT_NAMES.YEARS_IN_HOME]}
                placeholder=''
                name={INPUT_NAMES.YEARS_IN_HOME}
                label='Years in home'
                labelIconButton={
                  <InfoButton
                    className={styles.infoIcons}
                    onClick={() =>
                      handleInfoClick(MODAL_FIELD_INFO.YEARS_IN_HOME)
                    }
                  />
                }
                input={YearsInput}
                error={errors && errors[INPUT_NAMES.YEARS_IN_HOME]}
                onChange={onChange}
                autoFocus={focusedField === INPUT_NAMES.YEARS_IN_HOME}
                {...restProps}
              />

              <FormField
                className={styles.rentableNights}
                value={values && values[INPUT_NAMES.RENTABLE_NIGHTS]}
                placeholder='0'
                name={INPUT_NAMES.RENTABLE_NIGHTS}
                label='Rentable nights'
                labelIconButton={
                  <InfoButton
                    className={styles.infoIcons}
                    onClick={() =>
                      handleInfoClick(MODAL_FIELD_INFO.RENTABLE_NIGHTS)
                    }
                  />
                }
                input={NightsInput}
                error={errors && errors[INPUT_NAMES.RENTABLE_NIGHTS]}
                onChange={onChange}
                autoFocus={focusedField === INPUT_NAMES.RENTABLE_NIGHTS}
                {...restProps}
              />
            </div>
          </section>
        </div>
      </form>
      <FormModal isOpen={showInfoModal} setIsOpen={setShowInfoModal} />
    </>
  )
}

HomeownerForm.propTypes = {
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  focusedField: PropTypes.string,
  values: PropTypes.shape({
    maxBudget: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    downPayment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    yearsInHome: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currentMonthlyCost: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    rentableNights: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  errors: PropTypes.shape({
    maxBudget: PropTypes.string,
    downPayment: PropTypes.string,
    yearsInHome: PropTypes.string,
    currentMonthlyCost: PropTypes.string,
    rentableNights: PropTypes.string,
  }),
}

export default HomeownerForm
